import React, { useState, useEffect } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import { ClipboardList, ReceiptText, Info, X, ChevronLeft, ChevronRight, Eye, SlidersHorizontal } from "lucide-react";
import { useTransactionsQuery, useBonusTypesQuery } from "hooks/useWallet";
import Cookies from "js-cookie";
import { getUID } from "utils/Helper";
import { formatUserDateTime } from "utils/timezoneHelper";

const PageContainer = styled.div`
  min-height: 80vh;
  color: #fff;
  padding: 24px 16px;
  font-family: Arial, sans-serif;
  max-width: 1100px;
  margin: 24px auto 0;
  @media (max-width: 768px) {
    padding: 16px 12px;
    margin-top: 16px;
  }
`;

const ContentCard = styled.div`
  width: 100%;
`;

const TopHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 24px;
  font-weight: 800;
  color: #fff;
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #b9c4c7;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  &:hover {
    color: #fff;
    background-color: #292d2e;
  }
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 24px;
  min-width: 0;
  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
`;

const Sidebar = styled.aside`
  background-color: #122129;
  padding: 8px;
  border-radius: 8px;
  height: fit-content;
  border: 1px solid rgba(255, 255, 255, 0.05);
  @media (max-width: 768px) {
    position: static;
    margin-bottom: 4px;
  }
`;

const SidebarNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  @media (max-width: 768px) {
    flex-direction: row;
    overflow-x: auto;
    white-space: nowrap;
    gap: 8px;
    padding-bottom: 4px;
    padding-left: 2px;
    padding-right: 2px;
    
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const SidebarLink = styled(Link)`
  display: block;
  padding: 12px 16px;
  color: ${props => props.active ? "#fff" : "#b9c4c7"};
  background-color: ${props => props.active ? "#2f4553" : "transparent"};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  white-space: nowrap;
  border: ${props => props.active ? "1px solid #4e606d" : "1px solid transparent"};
  &:hover {
    color: #fff;
    background-color: ${props => props.active ? "#2f4553" : "rgba(255, 255, 255, 0.04)"};
    text-decoration: none;
  }
  @media (max-width: 768px) {
    display: inline-block;
    padding: 8px 12px;
    font-size: 13px;
    border: 1px solid ${props => props.active ? "#4e606d" : "rgba(255, 255, 255, 0.08)"};
  }
`;

const Panel = styled.section`
  min-width: 0;
  width: 100%;
`;

const SubTabs = styled.div`
  display: flex;
  gap: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  padding-bottom: 12px;
  margin-bottom: 8px;
  overflow-x: auto;
  white-space: nowrap;
  
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const SubTabButton = styled(Link)`
  padding: 8px 16px;
  border-radius: 9999px;
  color: ${props => props.active ? "#fff" : "#b9c4c7"};
  background-color: ${props => props.active ? "#2f4553" : "transparent"};
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.2s ease;
  border: ${props => props.active ? "1px solid #4e606d" : "1px solid transparent"};
  &:hover {
    color: #fff;
    background-color: ${props => props.active ? "#2f4553" : "rgba(255, 255, 255, 0.04)"};
    text-decoration: none;
  }
`;

const TableCard = styled.div`
  background-color: #122129;
  border-radius: 8px;
  padding: 16px;
  overflow-x: auto;
  max-width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.05);
  @media (max-width: 768px) {
    padding: 8px;
  }
`;

const TableContent = styled.div`
  min-width: 640px;
`;

const Row5 = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  padding: 12px 8px;
  font-size: 14px;
  font-weight: ${props => props.header ? "bold" : "normal"};
  color: ${props => props.header ? "#b9c4c7" : "#fff"};
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  align-items: center;
  transition: background-color 0.2s ease;
  
  ${props => !props.header && `
    &:hover {
      background-color: rgba(255, 255, 255, 0.02);
    }
  `}
`;

const Row6 = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr 1.2fr 1fr;
  padding: 12px 8px;
  font-size: 14px;
  font-weight: ${props => props.header ? "bold" : "normal"};
  color: ${props => props.header ? "#b9c4c7" : "#fff"};
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  align-items: center;
  transition: background-color 0.2s ease;
  
  ${props => !props.header && `
    &:hover {
      background-color: rgba(255, 255, 255, 0.02);
    }
  `}
  @media (max-width: 768px) {
    font-size: 12.5px;
    padding: 10px 4px;
  }
`;

const Row4 = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  padding: 12px 8px;
  font-size: 14px;
  font-weight: ${props => props.header ? "bold" : "normal"};
  color: ${props => props.header ? "#b9c4c7" : "#fff"};
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  align-items: center;
`;

const Row3 = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  padding: 12px 8px;
  font-size: 14px;
  font-weight: ${props => props.header ? "bold" : "normal"};
  color: ${props => props.header ? "#b9c4c7" : "#fff"};
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  align-items: center;
`;

const ColLeft = styled.span`
  text-align: left;
`;

const ColCenter = styled.span`
  text-align: center;
  ${props => props.bold && "font-weight: bold;"}
  ${props => props.color && `color: ${props.color};`}
`;

const ColRight = styled.span`
  text-align: right;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  background-color: ${props => {
    switch (props.status?.toLowerCase()) {
      case "accepted":
      case "completed":
      case "success":
        return "rgba(31, 193, 107, 0.15)";
      case "pending":
        return "rgba(255, 193, 7, 0.15)";
      default:
        return "rgba(244, 67, 54, 0.15)";
    }
  }};
  color: ${props => {
    switch (props.status?.toLowerCase()) {
      case "accepted":
      case "completed":
      case "success":
        return "#1fc16b";
      case "pending":
        return "#ffc107";
      default:
        return "#ef4444";
    }
  }};
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  color: #b9c4c7;
`;

const InfoBox = styled.div`
  display: flex;
  gap: 12px;
  background-color: #122129;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 14px;
  color: #b9c4c7;
  line-height: 1.5;
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const Loader = styled.div`
  border: 4px solid rgba(255,255,255,0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #1fc16b;
  animation: spin 1s linear infinite;
  margin: 40px auto;
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Filter System Styled Components
const ToggleFiltersButton = styled.button`
  display: none;
  background-color: #122129;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
  width: 100%;
  transition: all 0.2s ease;
  outline: none;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.04);
  }
  
  @media (max-width: 768px) {
    display: flex;
  }
`;

const FilterOverlay = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: ${props => props.show ? "block" : "none"};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(3px);
    z-index: 999998;
  }
`;

const DrawerHeader = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    padding-bottom: 16px;
    margin-bottom: 8px;
    width: 100%;
    
    .title {
      font-size: 16px;
      font-weight: 800;
      color: #fff;
    }
    
    .close-btn {
      background: none;
      border: none;
      color: #b1bad3;
      cursor: pointer;
      padding: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`;

const FilterBar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
  background-color: #122129;
  padding: 16px;
  border-radius: 8px;
  align-items: end;
  border: 1px solid rgba(255, 255, 255, 0.05);

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 280px;
    max-width: 85vw;
    grid-template-columns: 1fr;
    align-content: start;
    gap: 16px;
    padding: 24px 20px;
    background-color: #0f212e;
    border-left: 1px solid rgba(255, 255, 255, 0.08);
    box-shadow: -10px 0 30px rgba(0, 0, 0, 0.6);
    z-index: 999999;
    margin-bottom: 0;
    border-radius: 0;
    border-top: none;
    border-bottom: none;
    border-right: none;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    transform: ${props => props.show ? "translateX(0)" : "translateX(100%)"};
    display: grid;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 11px;
  color: #b9c4c7;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SelectInput = styled.select`
  background-color: #09161c;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s;
  &:focus {
    border-color: #1475e1;
  }
  @media (max-width: 768px) {
    padding: 10px 12px;
    font-size: 13px;
  }
`;

const TextInput = styled.input`
  background-color: #09161c;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
  outline: none;
  transition: border-color 0.2s;
  &:focus {
    border-color: #1475e1;
  }
  @media (max-width: 768px) {
    padding: 10px 12px;
    font-size: 13px;
  }
`;

const DateInput = styled.input`
  background-color: #09161c;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 13px;
  outline: none;
  color-scheme: dark;
  transition: border-color 0.2s;
  &:focus {
    border-color: #1475e1;
  }
  @media (max-width: 768px) {
    padding: 9px 12px;
    font-size: 13px;
  }
`;

// Pagination Styled Components
const PaginationBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  flex-wrap: wrap;
  gap: 12px;
  padding: 4px 8px;

  @media (max-width: 768px) {
    justify-content: center;
    flex-direction: column;
    text-align: center;
    gap: 8px;
  }
`;

const PaginationButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PageButton = styled.button`
  background-color: ${props => props.active ? "#1fc16b" : "#3a4142"};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: ${props => props.disabled ? "not-allowed" : "pointer"};
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: all 0.2s;
  &:hover:not(:disabled) {
    background-color: ${props => props.active ? "#1fc16b" : "#1fc16b"};
  }
`;

const PageInfoText = styled.span`
  font-size: 13px;
  color: #b9c4c7;
`;

const ViewButton = styled.button`
  background: none;
  border: none;
  color: #1fc16b;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  &:hover {
    background-color: rgba(31, 193, 107, 0.15);
    transform: scale(1.05);
  }
`;

// Details Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
  padding: 16px;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background-color: #232626;
  border-radius: 12px;
  width: 100%;
  max-width: 480px;
  border: 1px solid rgba(58, 65, 66, 0.6);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  animation: modalEnter 0.25s ease-out;

  @keyframes modalEnter {
    from {
      transform: scale(0.95);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background-color: #292d2e;
  border-bottom: 1px solid rgba(58, 65, 66, 0.4);
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ModalClose = styled.button`
  background: none;
  border: none;
  color: #b9c4c7;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
  &:hover {
    color: #fff;
    background-color: rgba(58, 65, 66, 0.6);
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-height: 60vh;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(58, 65, 66, 0.2);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(58, 65, 66, 0.8);
    border-radius: 3px;
  }
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(58, 65, 66, 0.2);
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  color: #b9c4c7;
  font-weight: 600;
  font-size: 13px;
`;

const DetailValue = styled.span`
  color: #fff;
  font-weight: 500;
  text-align: right;
  word-break: break-all;
  max-width: 250px;
  font-size: 13.5px;
`;

const SECTIONS = [
  { slug: "deposits", label: "Deposits" },
  { slug: "withdrawals", label: "Withdrawals" },
  { slug: "bonuses", label: "Bonuses" },
  /* { slug: "raffles", label: "Raffles" },
  { slug: "races", label: "Races" },
  { slug: "other", label: "Other" } */
];

const BONUS_SUBS = [
  { slug: "all", label: "All Bonuses" },
  { slug: "manual", label: "Manual" },
  { slug: "promotional", label: "Promotional" },
  { slug: "drop", label: "Drop" },
  { slug: "rakeback", label: "Rakeback" },
  { slug: "reload", label: "Reload" },
  { slug: "racePayout", label: "Race Payout" },
  { slug: "sportsbookPromotion", label: "Sportsbook Promotion" },
  { slug: "unclaimedPromotional", label: "Unclaimed Promotional" }
];

const OTHER_SUBS = [
  { slug: "all", label: "All Other" },
  { slug: "tipsReceived", label: "Tips Received" },
  { slug: "vaultDeposit", label: "Vault Deposit" },
  { slug: "vaultWithdrawal", label: "Vault Withdrawal" },
  { slug: "campaignWithdrawal", label: "Campaign Withdrawal" },
  { slug: "rainReceived", label: "Rain Received" },
  { slug: "rainSent", label: "Rain Sent" }
];

function subsFor(section) {
  if (section === "other") return OTHER_SUBS;
  return null;
}

function emptyLabel(section) {
  if (section === "deposits") return "No Banking Deposits";
  if (section === "withdrawals") return "No Withdrawals";
  return "No entries.";
}

function Empty({ label }) {
  return (
    <EmptyStateContainer>
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none" style={{ opacity: 0.4 }}>
        <rect x="8" y="32" width="10" height="16" rx="2" fill="currentColor" />
        <rect x="23" y="22" width="10" height="26" rx="2" fill="currentColor" />
        <rect x="38" y="14" width="10" height="34" rx="2" fill="currentColor" />
        <circle cx="43" cy="14" r="3" fill="#1fc16b" />
      </svg>
      <div style={{ marginTop: "16px", fontSize: "14px" }}>{label}</div>
    </EmptyStateContainer>
  );
}

function ColumnHeader({ section, sub }) {
  if (section === "deposits" || section === "withdrawals" || section === "bonuses") {
    return (
      <Row6 header>
        <ColLeft>Date</ColLeft>
        <ColCenter>{section === "bonuses" ? "Type" : "Method"}</ColCenter>
        <ColCenter>Amount</ColCenter>
        <ColCenter>Currency</ColCenter>
        <ColCenter>Status</ColCenter>
        <ColRight>Action</ColRight>
      </Row6>
    );
  }
  if (section === "raffles") {
    return (
      <Row3 header>
        <ColLeft>Date</ColLeft>
        <ColCenter>Raffle Name</ColCenter>
        <ColRight>Tickets</ColRight>
      </Row3>
    );
  }
  if (section === "races") {
    return (
      <Row5 header>
        <ColLeft>Date</ColLeft>
        <ColCenter>Prize</ColCenter>
        <ColCenter>Race Name</ColCenter>
        <ColCenter>Position</ColCenter>
        <ColRight>Status</ColRight>
      </Row5>
    );
  }
  if (section === "other" && sub === "tipsReceived") {
    return (
      <Row4 header>
        <ColLeft>Date</ColLeft>
        <ColCenter>Amount</ColCenter>
        <ColCenter>Type</ColCenter>
        <ColRight>Received from</ColRight>
      </Row4>
    );
  }
  return (
    <Row3 header>
      <ColLeft>Date</ColLeft>
      <ColCenter>Amount</ColCenter>
      <ColRight>Type</ColRight>
    </Row3>
  );
}

const getPresetDates = (preset) => {
  const today = new Date();
  const format = (d) => d.toISOString().split("T")[0];

  switch (preset) {
    case "today":
      return { start: format(today), end: format(today) };
    case "yesterday": {
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      return { start: format(yesterday), end: format(yesterday) };
    }
    case "last7": {
      const last7 = new Date();
      last7.setDate(today.getDate() - 7);
      return { start: format(last7), end: format(today) };
    }
    case "all":
    default:
      return { start: "", end: "" };
  }
};

function TransactionsContent() {
  const { section = "deposits", sub } = useParams();
  const history = useHistory();
  const uid = getUID || parseFloat(Cookies.get("uid"));

  // Filters State
  const [datePreset, setDatePreset] = useState("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currencyFilter, setCurrencyFilter] = useState("all");
  const [searchFilter, setSearchFilter] = useState("");
  const [limitFilter, setLimitFilter] = useState(10);
  const [bonusTypeFilter, setBonusTypeFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [selectedTx, setSelectedTx] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const { data: bonusTypes = [] } = useBonusTypesQuery(section === "bonuses" ? uid : null);

  // Compute final start and end dates
  const dates = getPresetDates(datePreset);
  const startDate = datePreset === "custom" ? customStart : dates.start;
  const endDate = datePreset === "custom" ? customEnd : dates.end;

  // Build active filters object for query
  const queryTab = section === "deposits" ? "Deposit" : section === "withdrawals" ? "Withdraw" : section === "bonuses" ? "Bonuses" : null;
  const filters = {
    page,
    limit: limitFilter,
    startDate,
    endDate,
    status: section === "bonuses" ? bonusTypeFilter : statusFilter,
    currency: currencyFilter,
    search: searchFilter,
    paginated: true
  };

  const { data = {}, isLoading: isQueryLoading, error } = useTransactionsQuery(queryTab, uid, filters);
  const isLoading = queryTab ? isQueryLoading : false;

  const transactionData = data.data || [];
  const pagination = data.pagination || { currentPage: 1, pageSize: 10, totalRecords: 0, totalPages: 1 };

  const subs = subsFor(section);
  const activeSub = subs ? (sub || subs[0].slug) : undefined;

  const close = () => {
    history.push("/");
  };

  // Reset pagination when filter changes
  useEffect(() => {
    setPage(1);
  }, [section, datePreset, customStart, customEnd, statusFilter, currencyFilter, searchFilter, limitFilter]);

  return (
    <ContentCard>
      <TopHeader>
        <HeaderTitle>
          <ReceiptText size={26} style={{ color: "#ffffff" }} />
          Transactions
        </HeaderTitle>
      </TopHeader>

      <MainGrid>
        <Sidebar>
          <SidebarNav>
            {SECTIONS.map(sec => (
              <SidebarLink
                key={sec.slug}
                to={`/transactions/${sec.slug}`}
                active={section === sec.slug ? 1 : 0}
              >
                {sec.label}
              </SidebarLink>
            ))}
          </SidebarNav>
        </Sidebar>

        <Panel>
          {subs && (
            <SubTabs>
              {subs.map(s => (
                <SubTabButton
                  key={s.slug}
                  to={`/transactions/${section}/${s.slug}`}
                  active={activeSub === s.slug ? 1 : 0}
                >
                  {s.label}
                </SubTabButton>
              ))}
            </SubTabs>
          )}

          {section === "bonuses" && bonusTypeFilter === "unclaimedPromotional" && (
            <InfoBox>
              <Info size={20} style={{ color: "#1fc16b", shrink: 0 }} />
              This page only displays bonuses that have been active for more than 96 hours. To receive bonuses earlier, make sure you're subscribed to our email, Telegram, and social media notifications.
            </InfoBox>
          )}

          {/* Filter Bar (Only show for deposits & withdrawals) */}
          {queryTab && (
            <>
              <ToggleFiltersButton
                onClick={() => setShowMobileFilters(s => !s)}
                style={{
                  border: "1px solid rgba(255, 255, 255, 0.15)",
                  padding: "10px 20px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  width: "100%",
                  justifyContent: "center",
                  borderRadius: "8px",
                  backgroundColor: "#122129",
                  color: "#fff",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  marginBottom: "12px",
                  outline: "none",
                }}
              >
                <SlidersHorizontal size={18} style={{ color: "#1fc16b" }} />
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </ToggleFiltersButton>
              <FilterOverlay show={showMobileFilters} onClick={() => setShowMobileFilters(false)} />
              <FilterBar show={showMobileFilters}>
                <DrawerHeader>
                  <span className="title">Filters</span>
                  <button className="close-btn" onClick={() => setShowMobileFilters(false)}>
                    <X size={20} />
                  </button>
                </DrawerHeader>
                <FilterGroup>
                  <Label>Search ID</Label>
                  <TextInput
                    type="text"
                    placeholder="Search Ref / ID..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                  />
                </FilterGroup>

                <FilterGroup>
                  <Label>Date Preset</Label>
                  <SelectInput value={datePreset} onChange={(e) => setDatePreset(e.target.value)}>
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="last7">Last 7 Days</option>
                    <option value="custom">Custom Range</option>
                  </SelectInput>
                </FilterGroup>

                {datePreset === "custom" && (
                  <>
                    <FilterGroup>
                      <Label>Start Date</Label>
                      <DateInput
                        type="date"
                        value={customStart}
                        onChange={(e) => setCustomStart(e.target.value)}
                      />
                    </FilterGroup>
                    <FilterGroup>
                      <Label>End Date</Label>
                      <DateInput
                        type="date"
                        value={customEnd}
                        onChange={(e) => setCustomEnd(e.target.value)}
                      />
                    </FilterGroup>
                  </>
                )}

                {section === "bonuses" ? (
                  <FilterGroup>
                    <Label>Bonus Type</Label>
                    <SelectInput value={bonusTypeFilter} onChange={(e) => setBonusTypeFilter(e.target.value)}>
                      <option value="all">All Bonuses</option>
                      {bonusTypes.map((type) => (
                        <option key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ')}
                        </option>
                      ))}
                    </SelectInput>
                  </FilterGroup>
                ) : (
                  <FilterGroup>
                    <Label>Status</Label>
                    <SelectInput value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                      <option value="all">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="accepted">Accepted / Success</option>
                      <option value="rejected">Rejected / Failed</option>
                    </SelectInput>
                  </FilterGroup>
                )}

                <FilterGroup>
                  <Label>Currency</Label>
                  <SelectInput value={currencyFilter} onChange={(e) => setCurrencyFilter(e.target.value)}>
                    <option value="all">All Currencies</option>
                    <option value="inr">INR</option>
                    <option value="usdt">USDT</option>
                    <option value="trx">TRX</option>
                  </SelectInput>
                </FilterGroup>

                <FilterGroup>
                  <Label>Rows Per Page</Label>
                  <SelectInput value={limitFilter} onChange={(e) => setLimitFilter(Number(e.target.value))}>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </SelectInput>
                </FilterGroup>
              </FilterBar>
            </>
          )}

          <TableCard>
            <TableContent>
              <ColumnHeader section={section} sub={activeSub} />

              {isLoading && queryTab && <Loader />}

              {!isLoading && (error || !queryTab || transactionData.length === 0) && (
                <Empty label={emptyLabel(section)} />
              )}

              {!isLoading && queryTab && transactionData.length > 0 && (
                <div>
                  {transactionData.map((tx, idx) => (
                    <Row6 key={idx}>
                      <ColLeft>
                        {formatUserDateTime(tx.created_at || tx.date)}
                      </ColLeft>
                      <ColCenter style={{ textTransform: "capitalize", fontSize: "13px" }}>
                        {tx.gateway ? tx.gateway.toLowerCase().replace(/_/g, " ") : "System"}
                      </ColCenter>
                      <ColCenter bold color="#1fc16b">
                        {tx.amount}
                      </ColCenter>
                      <ColCenter style={{ fontWeight: 600 }}>
                        {tx.currency ? tx.currency.toUpperCase() : "—"}
                      </ColCenter>
                      <ColCenter>
                        <StatusBadge status={tx.status}>{tx.status}</StatusBadge>
                      </ColCenter>
                      <ColRight>
                        <ViewButton onClick={() => setSelectedTx(tx)} title="View Details">
                          <Eye size={18} />
                        </ViewButton>
                      </ColRight>
                    </Row6>
                  ))}
                </div>
              )}
            </TableContent>
          </TableCard>

          {/* Pagination Bar (Only show for deposits & withdrawals when there is data) */}
          {!isLoading && queryTab && transactionData.length > 0 && (
            <PaginationBar>
              <PageInfoText>
                Showing {((page - 1) * limitFilter) + 1} to {Math.min(page * limitFilter, pagination.totalRecords)} of {pagination.totalRecords} entries
              </PageInfoText>
              <PaginationButtons>
                <PageButton
                  disabled={page === 1}
                  onClick={() => setPage(p => Math.max(p - 1, 1))}
                >
                  <ChevronLeft size={16} /> Prev
                </PageButton>
                <PageButton active>
                  {page}
                </PageButton>
                <PageButton
                  disabled={page >= pagination.totalPages}
                  onClick={() => setPage(p => Math.min(p + 1, pagination.totalPages))}
                >
                  Next <ChevronRight size={16} />
                </PageButton>
              </PaginationButtons>
            </PaginationBar>
          )}
        </Panel>
      </MainGrid>

      {/* Transaction Details Modal */}
      {selectedTx && (
        <ModalOverlay onClick={() => setSelectedTx(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Transaction Details</ModalTitle>
              <ModalClose onClick={() => setSelectedTx(null)}>
                <X size={20} />
              </ModalClose>
            </ModalHeader>
            <ModalBody>
              <DetailRow>
                <DetailLabel>Transaction Type</DetailLabel>
                <DetailValue style={{ textTransform: "capitalize" }}>
                  {section === "bonuses" ? "Bonus" : section === "deposits" ? "Deposit" : section === "withdrawals" ? "Withdrawal" : section}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>ID</DetailLabel>
                <DetailValue>{selectedTx.id || "—"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Date & Time</DetailLabel>
                <DetailValue>{formatUserDateTime(selectedTx.created_at || selectedTx.date)}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Amount</DetailLabel>
                <DetailValue style={{ color: "#1fc16b", fontWeight: "bold" }}>{selectedTx.amount}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Currency</DetailLabel>
                <DetailValue>{selectedTx.currency ? selectedTx.currency.toUpperCase() : "—"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Gateway / Method</DetailLabel>
                <DetailValue style={{ textTransform: "capitalize" }}>
                  {selectedTx.gateway ? selectedTx.gateway.toLowerCase().replace(/_/g, " ") : "System"}
                </DetailValue>
              </DetailRow>
              {section === "withdrawals" && selectedTx.withdraw_method && (
                <DetailRow>
                  <DetailLabel>Withdraw Mode</DetailLabel>
                  <DetailValue style={{ textTransform: "uppercase" }}>{selectedTx.withdraw_method}</DetailValue>
                </DetailRow>
              )}
              <DetailRow>
                <DetailLabel>
                  {section === "deposits" || section === "bonuses"
                    ? "Reference ID"
                    : (selectedTx.withdraw_method === "bank" || selectedTx.gateway?.toLowerCase().includes("bank"))
                      ? "Bank Account Number"
                      : (selectedTx.withdraw_method === "card" || selectedTx.gateway?.toLowerCase().includes("card"))
                        ? "Card Number"
                        : "Destination Address"}
                </DetailLabel>
                <DetailValue style={{ fontFamily: "monospace", color: "#b9c4c7" }}>{selectedTx.txid || "—"}</DetailValue>
              </DetailRow>

              {/* Dynamic Admin/User Custom Fields */}
              {(() => {
                let details = {};
                if (selectedTx.payment_details) {
                  try {
                    details = typeof selectedTx.payment_details === "string"
                      ? JSON.parse(selectedTx.payment_details)
                      : selectedTx.payment_details;
                  } catch (e) { }
                }

                const standardKeys = ["currency", "gateway_id", "gateway_name", "coin", "confirmations", "salt", "isManualApproval", "extra_fields"];
                const customFields = [];

                if (details && typeof details === "object") {
                  Object.keys(details).forEach(key => {
                    if (!standardKeys.includes(key) && details[key] !== null && details[key] !== undefined) {
                      customFields.push({ label: key, value: details[key] });
                    }
                  });

                  if (details.extra_fields && typeof details.extra_fields === "object") {
                    Object.keys(details.extra_fields).forEach(key => {
                      if (details.extra_fields[key] !== null && details.extra_fields[key] !== undefined) {
                        customFields.push({ label: key, value: details.extra_fields[key] });
                      }
                    });
                  }
                }

                return customFields.map((field, index) => (
                  <DetailRow key={index}>
                    <DetailLabel style={{ textTransform: "capitalize" }}>
                      {field.label.replace(/_/g, " ")}
                    </DetailLabel>
                    <DetailValue>{typeof field.value === "object" ? JSON.stringify(field.value) : String(field.value)}</DetailValue>
                  </DetailRow>
                ));
              })()}

              <DetailRow>
                <DetailLabel>Status</DetailLabel>
                <DetailValue>
                  <StatusBadge status={selectedTx.status}>{selectedTx.status}</StatusBadge>
                </DetailValue>
              </DetailRow>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </ContentCard>
  );
}

export default function Transactions(props) {
  const { section = "deposits" } = useParams();
  return (
    <>
      <Helmet>
        <title>Transactions — {section.charAt(0).toUpperCase() + section.slice(1)}</title>
      </Helmet>
      <PageContainer>
        <TransactionsContent />
      </PageContainer>
    </>
  );
}
